import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { TASK_WORDS, BILLED_WORDS, STAGE_WORDS } from './vocabulary'
import { PRODUCT_LAGS } from '../StreamIngest/vocabulary'

// What a span cost and how long it took — the proto's "cost & job" pane, from
// the span's ledger. **The cost is the span's, not a card's** (owner,
// 2026-10-05): transcription and the selector pay for every moment in the
// span, refused ones included, so a card shows its span's spend beside how
// many cards came from it. The judge is one total, not split per moment.
//
// Spend is summed over every pass of the selection job; the stage timings are
// the last pass's only (the trace, which every pass overwrites), and say so.
//
// Three parts, which the moments page's two views fold apart: the spend
// (`CostTable`), the moment's own lags (`MomentTiming`, no ledger needed) and
// the span's (`SpanTiming`). `SpanCost` lays them side by side, as a card and
// the router panel show them.

// The API's datetimes are naive UTC ("2026-10-05T12:00:00.123"); read them as
// UTC, or a lag against an epoch-ms field is off by the browser's offset.
const utcMs = (value) => {
  if (value == null) return null
  return Date.parse(/(Z|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`)
}

const lag = (later, earlier) =>
  later != null && earlier != null ? (later - earlier) / 1000 : null

const seconds = (s) => {
  if (s == null || Number.isNaN(s)) return '—'
  if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)} s`
  if (s < 3600) {
    return `${Math.floor(s / 60)}m ${String(Math.floor(s % 60)).padStart(
      2,
      '0'
    )}s`
  }
  return `${Math.floor(s / 3600)}h ${String(
    Math.floor((s % 3600) / 60)
  ).padStart(2, '0')}m`
}

const usd = (value) => (value == null ? '—' : `$${value.toFixed(4)}`)

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

// The tables' total on one line, for where they are folded away (the
// two-pane layout's Cost header).
export const spanCostLine = (ledger) => {
  const cost = ledger.cost
  if (!cost) return null
  return `span ${cost.transcript_missing && cost.usd != null ? '≥ ' : ''}${usd(
    cost.usd
  )} · ${plural(ledger.cards, 'card')} from it`
}

// The moment's own lags: Stream Ingest's product latencies (`PRODUCT_LAGS`,
// their names and definitions) for this one moment, the same plain
// differences the server takes. Each answers in words where there is no
// number: no push recorded (push off, not in the port, withheld, failed, or
// before 4b), no pull yet, not kept. A phone can pull the moment on its
// changes sync before the push, and that reads as such rather than negative.
const MOMENT_LAGS = {
  moment_to_surfaced: (m) => seconds(lag(m.surfaced_at_ms, m.moment_end_ms)),
  moment_to_push: (m) =>
    m.notified_at_ms == null
      ? 'no push'
      : seconds(lag(m.notified_at_ms, m.moment_end_ms)),
  push_to_fetch: (m) => {
    if (m.notified_at_ms == null) return '—'
    if (m.fetched_at_ms == null) return 'not read yet'
    const s = lag(m.fetched_at_ms, m.notified_at_ms)
    return s < 0 ? `read ${seconds(-s)} before it` : seconds(s)
  },
  surfaced_to_released: (m) =>
    m.released_at_ms == null
      ? 'not kept'
      : seconds(lag(m.released_at_ms, m.surfaced_at_ms)),
}

// The moment's number on one line, for its fold (the two-pane layout's
// Timing header): the end of the moment in the room to the push about it.
export const momentTimingLine = (moment) =>
  moment.notified_at_ms == null
    ? `no push · moment → surfaced ${MOMENT_LAGS.moment_to_surfaced(moment)}`
    : `moment → push ${MOMENT_LAGS.moment_to_push(moment)}`

export const MomentTiming = ({ moment }) => (
  <Table size="small">
    <TableBody>
      {PRODUCT_LAGS.filter((l) => MOMENT_LAGS[l.key]).map((l) => (
        <TableRow key={l.key}>
          <TableCell>
            <Tooltip title={l.help} placement="left">
              <span>{l.label.toLowerCase()}</span>
            </Tooltip>
          </TableCell>
          <TableCell align="right">{MOMENT_LAGS[l.key](moment)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

// The span's number on one line, for its fold (the Spans view's Timing
// header): the end of the span to the selection pass that read it.
export const spanTimingLine = (ledger) =>
  `span end → selected ${seconds(
    lag(utcMs(ledger.selected_at), ledger.span_end_ms)
  )}`

const stageLabel = (stage, mine) => {
  if (stage.startsWith('moment:')) {
    const position = Number(stage.slice('moment:'.length))
    return `moment #${position + 1}${position === mine ? ' (this card)' : ''}`
  }
  return STAGE_WORDS[stage] || stage
}

// The span's own lags, the transcription provider's time and the last
// selection pass's stages; `moment` marks its stage.
export const SpanTiming = ({ ledger, moment }) => {
  const job = ledger.job || { moments: [], claims: 0 }
  const times = ledger.times || {}
  const mine = moment
    ? job.moments.findIndex((row) => row.moment_id === moment.id)
    : -1
  const lags = [
    [
      'span end → transcribed',
      lag(utcMs(ledger.transcribed_at), ledger.span_end_ms),
    ],
    [
      'transcribed → selected',
      lag(utcMs(ledger.selected_at), utcMs(ledger.transcribed_at)),
    ],
  ]
  const stages = Object.entries(times.last_pass || {})
  return (
    <>
      <Table size="small">
        <TableBody>
          {lags.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell>{label}</TableCell>
              <TableCell align="right">{seconds(value)}</TableCell>
            </TableRow>
          ))}
          {times.asr_s != null && (
            <TableRow>
              <TableCell>
                transcription provider answering
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  {' '}
                  · {plural(times.asr_runs, 'run')}
                </Typography>
              </TableCell>
              <TableCell align="right">{seconds(times.asr_s)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 1 }}
      >
        last selection pass
        {job.claims > 1 ? ` (of ${job.claims})` : ''}
      </Typography>
      {stages.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          no stage timings (no trace)
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {stages.map(([stage, value]) => (
              <TableRow key={stage}>
                <TableCell>{stageLabel(stage, mine)}</TableCell>
                <TableCell align="right">{seconds(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}

export const CostTable = ({ ledger }) => {
  const cost = ledger.cost
  if (!cost) return null
  const job = ledger.job || { moments: [], claims: 0 }
  return (
    <>
      {cost.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing recorded.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Billed</TableCell>
              <TableCell align="right">Calls</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Spend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cost.rows.map((row) => (
              <TableRow key={`${row.task}-${row.model}-${row.billed}`}>
                <TableCell>{TASK_WORDS[row.task] || row.task}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {row.model || '—'}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {BILLED_WORDS[row.billed] || row.billed}
                </TableCell>
                <TableCell align="right">{row.calls ?? '—'}</TableCell>
                <TableCell align="right">
                  {Math.round(row.quantity).toLocaleString()}
                </TableCell>
                <TableCell align="right">{usd(row.usd)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.5 }}
      >
        span cost{' '}
        <b>
          {cost.transcript_missing && cost.usd != null ? '≥ ' : ''}
          {usd(cost.usd)}
        </b>{' '}
        · {plural(ledger.cards, 'card')} from it · selection summed over{' '}
        {plural(job.claims, 'pass')}
      </Typography>
      {cost.transcript_missing && (
        <Typography variant="caption" color="warning.main" display="block">
          No transcript doc (they expire after 30 days, the span after 90):
          transcription is not counted.
        </Typography>
      )}
      {cost.unpriced?.length > 0 && (
        <Typography variant="caption" color="warning.main" display="block">
          Not on the price list: {cost.unpriced.join(', ')}
        </Typography>
      )}
    </>
  )
}

const SpanCost = ({ ledger, moment }) => {
  if (!ledger.cost) return null
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 1 }}>
      <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
        <Typography variant="subtitle2">What the span cost</Typography>
        <CostTable ledger={ledger} />
      </Box>
      <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
        <Typography variant="subtitle2">How long it took</Typography>
        {moment && <MomentTiming moment={moment} />}
        <SpanTiming ledger={ledger} moment={moment} />
      </Box>
    </Box>
  )
}

export default SpanCost
